/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { func, object } from 'prop-types';
import classNames from 'clsx';

import p from 'polyglot/polyglot';

import SpinnerIcon from 'components/SpinnerIcon/SpinnerIcon';
import Button from 'components/Button/Button';

import ChatBubble from './ChatBubble/ChatBubble';

import styles from './chat.scss';

/**
 * This is the Chat component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 * @author Niklas Rosdahl <niklas.rosdahl@vntrs.com>
 */

export default class Chat extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    message: object.isRequired,
    onSendMessage: func.isRequired,
    pollMessage: func.isRequired,
  };

  /**
   * Create Chat.
   *
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.timer = null;
    this.state = {
      message_text: '',
    };
  }

  componentDidMount() {
    const { message, pollMessage } = this.props;

    this.timer = pollMessage(message?.discussion_id);

    if (this.chat) {
      this.chat.scrollTop = this.chat.scrollHeight;
    }
  }

  componentDidUpdate() {
    const { message: nextMessage } = this.props;

    if (!nextMessage.loading && !nextMessage.sending) {
      process.nextTick(() => {
        if (this.chat) {
          this.chat.scrollTop = this.chat.scrollHeight;
        }
      });
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  onSubmit = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const {
      message: { sending },
      onSendMessage,
    } = this.props;

    if (sending) return;

    onSendMessage({ ...this.state });

    this.setState({ message_text: '' });
  };

  onChange = event => {
    this.setState({
      message_text: event.target.value,
    });
  };

  /**
   * Render Chat.
   *
   * @return {Object}
   */
  render() {
    const { message_text } = this.state;
    const { message } = this.props;
    const { loading, sending, messages, discussion_id, contract } = message;

    if (loading) {
      return (
        <section className={styles.wrapper}>
          <div className={styles.spinner}>
            <SpinnerIcon medium />
          </div>
        </section>
      );
    }

    return (
      <section className={styles.wrapper}>
        <div className={styles.content}>
          <div
            className={styles.messages}
            ref={ref => {
              this.chat = ref;
            }}
          >
            {messages.map(message => (
              <ChatBubble key={message.message_id} {...message} />
            ))}
          </div>
          {discussion_id === contract?.discussion_id &&
            contract?.accepted_by_client &&
            contract?.accepted_by_talent &&
            !contract?.is_canceled && (
              <div className={styles.acceptedContract}>
                <div className={styles.divider} />
                <span className={styles.acceptedText}>
                  🎉 Congrats! You’ve concluded the deal!
                </span>
                <div className={styles.divider} />
              </div>
            )}
          <form
            className={classNames(styles.form, {
              [styles.sending]: sending,
            })}
            onSubmit={this.onSubmit}
            noValidate
            autoComplete="off"
          >
            <textarea
              className={styles.input}
              name="message_text"
              placeholder={p.t('inbox.message.form.message_text.placeholder')}
              value={message_text}
              onChange={this.onChange}
            />

            <Button
              className={styles.button}
              onClick={this.onSubmit}
              sending={sending}
              small
            >
              {p.t('inbox.message.form.submit')}
            </Button>
          </form>
        </div>
      </section>
    );
  }
}
